import {
  AuthModule,
  ThirdPartyConnectionStatus,
  ThirdPartySystem,
} from '$constants'
import {
  ApiException,
  ConnectionInitiationDetail,
  ConnectionStatus,
  CoreApiError,
  ThirdPartyConnectionInfo,
} from '$models'
import { coreApi } from '$api'

// Each system has its own controller, so the generated client gives one method per system
const connectionApiBySystem: Record<
  ThirdPartySystem,
  {
    getConnectionDetails: (module?: AuthModule) => Promise<ConnectionStatus>
    initiateConnection: (
      module?: AuthModule
    ) => Promise<ConnectionInitiationDetail>
    detachConnection: () => Promise<void>
  }
> = {
  [ThirdPartySystem.Square]: {
    getConnectionDetails: (module) =>
      coreApi.getSquareConnectionDetails(module),
    initiateConnection: (module) => coreApi.initiateSquareConnection(module),
    detachConnection: () => coreApi.detachSquareConnection(),
  },
  [ThirdPartySystem.SquareConnector]: {
    getConnectionDetails: (module) =>
      coreApi.getSquareConnectorConnectionDetails(module),
    initiateConnection: (module) =>
      coreApi.initiateSquareConnectorConnection(module),
    detachConnection: () => coreApi.detachSquareConnectorConnection(),
  },
  [ThirdPartySystem.Xero]: {
    getConnectionDetails: (module) => coreApi.getXeroConnectionDetails(module),
    initiateConnection: (module) => coreApi.initiateXeroConnection(module),
    detachConnection: () => coreApi.detachXeroConnection(),
  },
}

/**
 * Converts an HTTP status code to a ThirdPartyConnectionStatus enum value
 * @param status - HTTP status code (200 = Connected, 404 = Disconnected, others = Unknown)
 * @returns ThirdPartyConnectionStatus enum value
 */
export function httpStatusToConnectionStatus(
  status?: number
): ThirdPartyConnectionStatus {
  if (!status) {
    return ThirdPartyConnectionStatus.Unknown
  }
  return status == 200
    ? ThirdPartyConnectionStatus.Connected
    : status == 404
    ? ThirdPartyConnectionStatus.Disconnected
    : ThirdPartyConnectionStatus.Unknown
}

// A 404 with an error body comes as a CoreApiError, anything else as an ApiException
function getErrorHttpStatus(error: unknown): number | undefined {
  if (error instanceof CoreApiError) {
    return error.code
  }
  if (error instanceof ApiException) {
    return error.status
  }
  return undefined
}

/**
 * Checks the connection status of an external system and converts the result
 * to a ThirdPartyConnectionInfo object. A missing connection (404) maps to
 * Disconnected and any unexpected failure maps to Unknown, so this never throws.
 * @param system - The external system to check
 * @param authModule - The auth module (for example Stock or Time)
 * @returns Promise resolving to ThirdPartyConnectionInfo
 */
export async function getExternalSystemConnectionInfo(
  system: ThirdPartySystem,
  authModule?: AuthModule | null
): Promise<ThirdPartyConnectionInfo> {
  try {
    const connectionStatus = await connectionApiBySystem[
      system
    ].getConnectionDetails(authModule ?? undefined)

    return {
      connectionStatus: ThirdPartyConnectionStatus.Connected,
      thirdPartyDisplayMetadata:
        connectionStatus.thirdPartyDisplayMetadata ?? {},
    }
  } catch (error) {
    return {
      connectionStatus: httpStatusToConnectionStatus(getErrorHttpStatus(error)),
      thirdPartyDisplayMetadata: {},
    }
  }
}

/**
 * Starts the OAuth authorization flow for an external system. Returns the
 * authorization page to send the user to.
 * @param system - The external system to connect to
 * @param authModule - The auth module (for example Stock or Time)
 */
export async function initiateExternalSystemConnection(
  system: ThirdPartySystem,
  authModule?: AuthModule | null
): Promise<ConnectionInitiationDetail> {
  return connectionApiBySystem[system].initiateConnection(
    authModule ?? undefined
  )
}

/**
 * Disconnects an external system and clears the stored tokens. For Xero this removes both
 * the accounting and the payroll connection, since they share one authorization.
 * @param system - The external system to disconnect
 */
export async function detachExternalSystemConnection(
  system: ThirdPartySystem
): Promise<void> {
  return connectionApiBySystem[system].detachConnection()
}
